// Epic 7 pixel parity (ADO-903) — the header search typeahead dropdown, ported
// from the prototype `ShoplinkSearchModule` (input shell L479-528, dropdown
// L530-534, rows L90-247, sections L536-586) and backed by the REAL
// `searchSuggestions` client (Story 7.2 seam: suggestions while typing, the full
// matches first (Bug 4198), then in product-code order as SL1.0 lists them, Bug 3731).
//
// LAYERING: this is an APP component (not design-system) because it consumes
// the search client and the basket/availability stores, which the design system
// must not import. It renders through SiteHeader's `searchTypeahead` slot into
// the search box's relative anchor, so the presentational header stays clean.
//
// Behaviour (spec I/O matrix):
//  • opens on input focus / every keystroke (the page owns the `open` flag);
//    non-blank query → up to 8 real result rows + "View all"; blank → the last
//    5 session-local COMMITTED search terms (real history only), or nothing.
//  • ~200ms debounce; every fetch aborts its predecessor via `AbortSignal`
//    (AbortError is a superseded fetch, not an error); a transport failure
//    closes the dropdown silently — never a broken dropdown.
//  • Escape / click-outside / clear / commit close it (the page clears `open`
//    for clear + commit; Escape and click-outside are handled here).
//  • a row's name/image commits that product's NAME as a search (no PDP yet);
//    "View all" commits the current query — both via `onCommit`.
//  • row "Add" = the SAME one-click basket semantics as ProductCard (Story 7.9
//    add + short "Added ✓" confirm; supplier-unavailable is store-gated); once
//    the product is in the cart the row shows the cart quantity stepper instead,
//    editing the line by the card's rules (decrease at 1 or a typed 0 removes it
//    and brings Add back). Only the supplier status blocks the add (DD-S.2): a
//    supplier-UNAVAILABLE row is muted and shows an INERT "Substitute" placeholder
//    instead (no dialog from the dropdown), while the IN/LOW/OUT stock bands are
//    advisory, so an out-of-stock row keeps its "Out of stock" label AND its Add, as
//    the product card does (SL1.0 `stock_and_subs.php:74-78`, "You can still order
//    this product"; Enabler E7-B, ADO-2273, OP-5). Left of the action, every row shows
//    its unit price through the shared price component (Figma 4002:14799 desktop,
//    4002:17759 mobile): the stand-in `provisionalUnitPrice` with real prices off.
//  • real prices on (Pricing migration 17, ADO-2248, DEC-3): the dropdown prices only the
//    rows it shows, in one request per settled term made after the search answers, on the
//    search's own `AbortSignal`, so a superseded search aborts its price request with it.
//    It reuses prices it already holds for repeated SKUs while it stays open, and drops
//    them when it closes. That is a dropdown-scoped lookup, not the shared price store:
//    the store's batched requests cannot be aborted per caller, and its entries outlive
//    the dropdown, which ADO-2248 DEC-3 rules out.
//  • the dropdown hydrates availability for its suggestion SKUs (useAvailability,
//    same as the listings) so the stock label and the supplier-unavailable
//    Substitute placeholder are right on the FIRST search, without relying on
//    another surface having fetched the SKU (2026-07-17 demo feedback).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Icon,
  ConflictMessage,
  QuantityStepper,
  type QuantityStepperCommit,
  useHeaderLayout,
} from '../../design-system';
import { searchSuggestions, type SearchResultProduct } from '../../api/search.client';
import type { ProductSummary } from '../../api/browse.client';
import type { AvailabilityBand } from '../../api/availability.client';
import { useAvailabilityStore } from '../../availability/useAvailabilityStore';
import { PICKED_FRESH_LABEL, showsPickedFresh } from '../../availability/pickedFresh';
import { useAvailability } from '../../availability/useAvailability';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { NoEntryPrompt } from '../../ordering/NoEntryPrompt';
import { useBasketStore } from '../../basket/useBasketStore';
import { useSessionMarginShown } from '../../basket/sessionCart';
import type { ConflictIntent } from '../../basket/conflictCopy';
import { useLineConflictMessage } from '../../basket/useLineConflictMessage';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import {
  isAtLineMaximum,
  sanitiseQuantityInput,
  typedLineQuantity,
} from '../../basket/lineQuantity';
import { ProductImage } from '../Browse/ProductImage';
import { packCaseLabel } from '../../basket/packCase';
import { fetchPriceQuotes } from '../../api/price-quotes.client';
import {
  addBlockedByPrice,
  priceIsLoading,
  ProductPriceFigure,
  useLoadingTooLong,
} from '../../pricing/ProductPrice';
import { realPricesEnabled } from '../../pricing/realPrices';
import { drawsItsPrice, isHiddenFromLists, LIST_PRICE_HOLD_MS } from '../../pricing/listVisibility';
import {
  priceEntryFromQuote,
  usePriceAccountCode,
  type PriceEntry,
} from '../../pricing/usePriceStore';
import { readRecentSearches } from './recentSearches';
import styles from './SearchTypeahead.module.css';

/** Prototype debounce window before a keystroke becomes a suggestion fetch. */
const DEBOUNCE_MS = 200;
/** Prototype cap: at most 8 product suggestion rows ("View all" for the rest). */
const MAX_RESULTS = 8;

/** The prices the open dropdown holds, for one ordering entry and trading account. */
interface DropdownPrices {
  readonly key: string;
  readonly entries: Readonly<Record<string, PriceEntry>>;
}

const NONE_WAITING: ReadonlySet<string> = new Set();
const NONE_DRAWN = (): boolean => false;

/** The suggestions the dropdown draws, or `settling` while one it needs has no answer yet. */
type SuggestionRows =
  | { readonly settling: true }
  | { readonly settling: false; readonly rows: readonly SearchResultProduct[] };

/**
 * Pricing migration 17b (ADO-3948): the rows the dropdown draws from the search's candidates, in
 * order, at most {@link MAX_RESULTS}. A product with no price of its own is left out, and so,
 * where `marginShown` (the session account sees its retail margin), is a priced product with no
 * RSP (ADO-4197, DEC-3; `isHiddenFromLists`), unless it is an exact code or barcode hit (DEC-1, a
 * lookup). The cap applies after hiding, so a hidden row's place goes to the next candidate. Settling while a candidate the walk reaches has no
 * answer yet: the dropdown draws its rows once, already filtered (DEC-2). A row whose Retry is in
 * flight (`loading`) has had an answer, so it never holds the rows again. A candidate that
 * `alreadyDrawn` matches is drawn whatever its answer, so a later answer never takes it away
 * (AC2): a row drawn on an earlier walk (its Retry may come back hidden), or a candidate still
 * unanswered when the hold ran out (`LIST_PRICE_HOLD_MS`).
 */
function suggestionRows(
  candidates: readonly SearchResultProduct[],
  entryOf: (sku: string) => PriceEntry | undefined,
  alreadyDrawn: (sku: string) => boolean,
  marginShown: boolean,
): SuggestionRows {
  const rows: SearchResultProduct[] = [];
  for (const product of candidates) {
    if (rows.length >= MAX_RESULTS) break;
    // A row that draws no price (an off-sale RBP parent shown for its links) is never hidden
    // and never holds the rows, as on the results page (`drawsItsPrice`).
    if (!drawsItsPrice(product) || alreadyDrawn(product.sku)) {
      rows.push(product);
      continue;
    }
    const entry = entryOf(product.sku);
    if (entry === undefined) return { settling: true };
    if (product.exactHit === true || !isHiddenFromLists(entry, marginShown)) rows.push(product);
  }
  return { settling: false, rows };
}

/** Band → dropdown stock label (prototype row meta vocabulary). */
const BAND_LABEL: Record<AvailabilityBand, string> = {
  IN_STOCK: 'In stock',
  LOW_STOCK: 'Low stock',
  OUT_OF_STOCK: 'Out of stock',
};

export interface SearchTypeaheadProps {
  /** The LIVE search box value (not the committed term). */
  query: string;
  /** Whether the dropdown is open (the page owns this — focus/keystroke set it,
   *  clear/commit unset it; Escape/click-outside call `onClose`). */
  open: boolean;
  /** The dropdown's DOM element id. PR #139 review: the suggestions are a labelled
   *  `role="group"` of ordinary buttons (not an ARIA combobox listbox), so this is
   *  just the group container's id, not an `aria-controls` target. */
  listboxId: string;
  onClose: () => void;
  /** Commit `term` as a search (the existing Products body swap) — used by the
   *  result rows (product name), recent-term rows and "View all". */
  onCommit: (term: string) => void;
}

/** One product suggestion row: thumbnail + SKU/name/meta + Add, the cart stepper or, for a
 *  supplier-unavailable product, Substitute. Subscribes per-SKU like ProductCard so
 *  basket/availability stay live. */
function TypeaheadResultRow({
  product,
  price,
  priceAsked,
  onRetryPrice,
  onCommit,
}: {
  readonly product: ProductSummary;
  /** This row's price state from the dropdown's lookup; `undefined` until it is answered. */
  readonly price: PriceEntry | undefined;
  /** Whether the dropdown asks for prices at all: no entry chosen means no request (PS-9). */
  readonly priceAsked: boolean;
  /** The failed state's Retry: ask again for this row's SKU only (ADO-2248 PS-4). */
  readonly onRetryPrice: (sku: string) => void;
  readonly onCommit: (term: string) => void;
}) {
  const availability = useAvailabilityStore((s) => s.entries[product.sku]);
  const basketPending = useBasketStore((s) => s.pending.has(product.sku));
  const inCart = useBasketStore((s) => s.quantityBySku.get(product.sku) ?? 0);
  const setLineQuantity = useBasketStore((s) => s.setQuantity);
  // While the first add is in flight Add stays (disabled), so the stepper never shows a
  // line the add has not landed yet.
  const [firstAddInFlight, setFirstAddInFlight] = useState(false);
  const showsCartStepper = inCart > 0 && !firstAddInFlight;
  // ADO-1779 — on mobile (Figma Search — Typeahead, Mobile 320, 4002:17759) the
  // row's Add is a "+" icon button, which leaves the name its room at 320px. The
  // accessible name and the status announcement are the same in both forms. A row
  // holding the cart stepper draws it compact (96x44) under the price.
  const iconAdd = useHeaderLayout() === 'mobile';
  // ADO-2248 (the price states, story owner 2026-10-28): a state's text is about twice a
  // price's width, so on mobile it stacks over the action, as the price does over the stepper,
  // and the name keeps its room. The early "€––.––" is a price's width and stays beside the
  // action, so the row does not move when the price lands; "Loading price…" stacks. The row
  // times the switch itself and hands it to the figure, so the two never disagree.
  const priceLoading = priceIsLoading(price, priceAsked);
  const loadingTooLong = useLoadingTooLong(priceLoading);
  const priceStateShown = addBlockedByPrice(price, priceAsked) && (!priceLoading || loadingTooLong);
  // Story 7.12 Iteration 2 (ADO-911), AC7 — QUICK-ADD is one of the three
  // add-to-basket entry points the PO's modal must fire from (PDP + quick-add +
  // add-from-listing), so the row passes the product name and gets the Linked
  // Product upsell exactly like every other Add in the app.
  // ADO-911 design refresh — a search add is non-invasive: it opens the TOAST,
  // never the full modal (the modal is PDP-only now).
  const { confirmed, confirm } = useAddConfirmation(product.sku, {
    productName: product.name,
    upsellVariant: 'toast',
  });

  // The only block on an add is the supplier status (DD-S.2): the band, OUT_OF_STOCK included,
  // is advisory and only picks the stock label, as on the product card.
  const supplierUnavailable = availability?.status === 'UNAVAILABLE';
  const band = availability?.band;
  // ADO-2249 (BWG feedback, 5 Nov): `{retailUnits} / {packSize}`, as on the card and the review.
  const packLine = packCaseLabel(product.retailUnits, product.packSize) ?? '';

  const stockClass =
    band === 'IN_STOCK' ? styles.stockIn : band === 'LOW_STOCK' ? styles.stockLow : styles.stockOut;

  // Swapping Add for the stepper (and back) unmounts the focused control. A swap asks for
  // focus on a control by its `data-control`, taken once that control is on screen, and
  // only while focus is still on this row's action (or dropped to <body> by the unmount).
  const actionRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<string | null>(null);
  const focusActionControl = (control: string): boolean => {
    const target = actionRef.current?.querySelector<HTMLElement>(`[data-control="${control}"]`);
    if (!target) return false;
    target.focus({ preventScroll: true });
    return document.activeElement === target;
  };
  const focusStayedOnTheAction = () =>
    document.activeElement === document.body ||
    actionRef.current?.contains(document.activeElement) === true;
  const requestFocus = (control: string) => {
    if (!focusStayedOnTheAction()) return;
    pendingFocusRef.current = focusActionControl(control) ? null : control;
  };
  useLayoutEffect(() => {
    const control = pendingFocusRef.current;
    if (control === null) return;
    if (!focusStayedOnTheAction()) pendingFocusRef.current = null;
    else if (focusActionControl(control)) pendingFocusRef.current = null;
  });

  const addOne = () => {
    setFirstAddInFlight(true);
    void addAndFocus(1);
  };
  // Story 8.3 (ADO-2012), D-19: an add refused because a colleague added the product first
  // leaves their line in the cart, so the stepper replaces Add and focus follows as for a
  // landed add.
  const addAndFocus = (quantity: number) =>
    confirm(quantity)
      .then((landed) => {
        const swapsToStepper =
          landed || (useBasketStore.getState().quantityBySku.get(product.sku) ?? 0) > 0;
        requestFocus(swapsToStepper ? 'increase' : 'add');
      })
      .finally(() => setFirstAddInFlight(false));
  // The stepper edits the cart line by the cart row's rules: decrease at 1 and a typed 0
  // remove the line (Add comes back, and focus with it), an emptied field restores the
  // quantity and saves nothing. If a removal does not land the stepper is back, and so is
  // focus. A typed 0 committed by leaving the field for another control moves no focus.
  const removeLine = async (control: string) => {
    const landed = setLineQuantity(product.sku, 0);
    requestFocus('add');
    if (await landed) return;
    pendingFocusRef.current = null;
    requestFocus(control);
  };
  const decreaseLine = () => {
    if (inCart <= 1) void removeLine('decrease');
    else void setLineQuantity(product.sku, inCart - 1);
  };
  const increaseLine = () => void setLineQuantity(product.sku, inCart + 1);
  const commitTypedLine = ({ typed, trigger, focusTarget }: QuantityStepperCommit) => {
    const next = typedLineQuantity(typed, inCart);
    if (next === null) return;
    if (next > 0) {
      void setLineQuantity(product.sku, next);
      return;
    }
    const focusLeftTheControl =
      trigger === 'blur' && focusTarget !== null && !actionRef.current?.contains(focusTarget);
    if (focusLeftTheControl) void setLineQuantity(product.sku, 0);
    else void removeLine('quantity');
  };

  // Story 8.3 (ADO-2012), Tasks 11.3 and 11.8: a refused add or change of this product's
  // line. No frame draws the typeahead, so the message takes a full-width line under the
  // row's content, above its divider (decided 2026-10-15). Its action is a new change to the
  // line, which ends the message; focus goes where the same change from the row would send it.
  // The action sits outside the row's controls, so it first moves focus to the control that
  // change starts from (as CartLineRow does); the row's own focus moves then follow from there.
  const shownConflict = useLineConflictMessage(product.sku, { blocked: supplierUnavailable });
  const conflictAction = shownConflict?.action ?? null;
  const runConflictAction = (intent: ConflictIntent) => {
    if (intent.type === 'remove') {
      // From Decrease, as the row's own removal: Add once it lands, the stepper if not.
      focusActionControl('decrease');
      void removeLine('decrease');
      return;
    }
    if (intent.type === 'add') {
      // "Add it back" shows only when the line is gone, so Add is on screen: from Add, as
      // the row's own add, focus moves to Increase once the add lands.
      focusActionControl('add');
      setFirstAddInFlight(true);
      void addAndFocus(intent.quantity);
      return;
    }
    focusActionControl('quantity');
    void setLineQuantity(product.sku, intent.quantity);
  };

  return (
    // PR #139 review: a plain row container, NOT a listbox `option` — the row hosts
    // multiple interactive controls (thumbnail, info, Add/Substitute), which a
    // single option can't legally contain. Each control is a real, labelled button.
    <div
      className={supplierUnavailable ? `${styles.row} ${styles.rowMuted}` : styles.row}
      data-conflict={shownConflict === null ? undefined : ''}
    >
      {/* Thumbnail — clicking commits the product's name as a search (no PDP). */}
      <button
        type="button"
        className={styles.thumb}
        onClick={() => onCommit(product.name)}
        aria-label={`Search for ${product.name}`}
      >
        <ProductImage src={product.imageUrl} alt={product.name} />
      </button>

      {/* SKU / name / meta — same commit-as-search click target. */}
      <button type="button" className={styles.info} onClick={() => onCommit(product.name)}>
        <span className={styles.sku}>{product.sku}</span>
        <span className={styles.name}>{product.name}</span>
        <span className={styles.metaLine}>
          {packLine !== '' && <span>{packLine}</span>}
          {/* Stock label once availability hydrates (the host hook fetches the
              suggestion SKUs; rows still render immediately without it). */}
          {band !== undefined && <span className={stockClass}>{BAND_LABEL[band]}</span>}
          {/* A picked-fresh product has no entry (E7-B AVL-5.4): SL1.0's "Picked fresh" in
              the label's place, in the meta line's own colour (Enabler E7-C, ADO-2274, D-16). */}
          {showsPickedFresh(product, availability) && <span>{PICKED_FRESH_LABEL}</span>}
        </span>
      </button>

      {/* On mobile a row holding the stepper stacks the price over it (Figma 4002:17759). */}
      <div
        className={styles.action}
        ref={actionRef}
        data-stacked={
          iconAdd && ((showsCartStepper && !supplierUnavailable) || priceStateShown)
            ? ''
            : undefined
        }
      >
        <ProductPriceFigure
          sku={product.sku}
          productName={product.name}
          entry={price}
          asked={priceAsked}
          onRetry={() => onRetryPrice(product.sku)}
          loadingTooLong={loadingTooLong}
          className={styles.price}
        />
        {supplierUnavailable ? (
          /* INERT placeholder — the substitutes dialog is a card affordance,
             never opened from the dropdown (customer-demo posture). */
          <Button
            variant="substitute"
            className={styles.rowCta}
            aria-label={`Substitutes for ${product.name} (coming soon)`}
          >
            Substitute
          </Button>
        ) : showsCartStepper ? (
          <QuantityStepper
            size={iconAdd ? 'compact' : 'default'}
            value={inCart}
            label={`Cart quantity for ${product.name}`}
            decreaseLabel={`Decrease quantity in cart of ${product.name}`}
            increaseLabel={`Increase quantity in cart of ${product.name}`}
            onDecrease={decreaseLine}
            onIncrease={increaseLine}
            increaseDisabled={isAtLineMaximum(inCart)}
            busy={basketPending}
            decreaseControl="decrease"
            increaseControl="increase"
            editable={{
              inputLabel: `Quantity in cart of ${product.name}`,
              sanitiseInput: sanitiseQuantityInput,
              onCommit: commitTypedLine,
              control: 'quantity',
            }}
          />
        ) : (
          <Button
            variant="primary"
            className={iconAdd ? `${styles.rowCta} ${styles.rowCtaIcon}` : styles.rowCta}
            data-control="add"
            onClick={addOne}
            disabled={
              basketPending ||
              firstAddInFlight ||
              supplierUnavailable ||
              addBlockedByPrice(price, priceAsked)
            }
            aria-label={`Add ${product.name} to basket`}
          >
            {iconAdd ? (
              <Icon name={confirmed ? 'check' : 'plus'} size={16} />
            ) : confirmed ? (
              'Added ✓'
            ) : (
              'Add'
            )}
          </Button>
        )}
        {/* Outside the swap, so the announcement survives Add giving way to the stepper. */}
        {!supplierUnavailable && (
          <span className={styles.visuallyHidden} role="status">
            {confirmed ? `Added ${product.name} to basket` : ''}
          </span>
        )}
      </div>
      {shownConflict === null ? null : (
        <ConflictMessage
          className={styles.conflict}
          message={shownConflict.message}
          {...(conflictAction === null
            ? {}
            : {
                action: {
                  label: conflictAction.label,
                  onClick: () => runConflictAction(conflictAction.intent),
                },
              })}
        />
      )}
    </div>
  );
}

export function SearchTypeahead({
  query,
  open,
  listboxId,
  onClose,
  onCommit,
}: SearchTypeaheadProps) {
  const trimmed = query.trim();
  const hostRef = useRef<HTMLDivElement>(null);
  // Enabler E7-C (ADO-2274), D-1: the same selected ordering entry the results page sends, so
  // the suggestions are that entry's range, and the server scopes its notAvailable rows (Story
  // 8.2, ADO-2011, AC6) to the entry's depot.
  const catalogueEntryKey = useCatalogueEntryKey();

  // The last successfully loaded suggestion set: every orderable candidate the search answered,
  // uncapped, so hiding can still fill the dropdown (ADO-3948). It survives while the NEXT fetch
  // is in flight (no flicker between keystrokes); a transport failure clears it so the dropdown
  // closes rather than showing anything broken.
  const [loaded, setLoaded] = useState<{
    readonly term: string;
    readonly products: readonly SearchResultProduct[];
  } | null>(null);

  // Pricing migration 17 (ADO-2248 DEC-3): the rows' prices, held only while the dropdown is
  // open and only for the entry and account they were asked under. The ref is what the fetch
  // reads for reuse; the state is what the rows render. Both are written together.
  // The account is the one every price request names, the selected tile's (DEC-10).
  // With the flag off (fixed at build time) the entry, the account and both keys are null, so
  // they never reach the search effect's dependencies and the dropdown behaves as before.
  const pricesOn = realPricesEnabled();
  const sessionAccountCode = usePriceAccountCode();
  const entryKey = pricesOn ? catalogueEntryKey : null;
  const accountCode = pricesOn ? sessionAccountCode : null;
  const priceKey = entryKey === null ? null : `${entryKey} ${accountCode ?? ''}`;
  // ADO-4197 DEC-3: a no-RSP row is left out only where the store sees its retail margin. The
  // fetch reads it through the ref, so a change of it does not search again.
  const marginShown = useSessionMarginShown();
  const marginShownRef = useRef(marginShown);
  useLayoutEffect(() => {
    marginShownRef.current = marginShown;
  }, [marginShown]);
  const [dropdownPrices, setDropdownPrices] = useState<DropdownPrices | null>(null);
  const dropdownPricesRef = useRef<DropdownPrices | null>(null);

  // ADO-3948: the rows drawn, once every one they need is answered. While a new term's rows
  // settle, the last drawn rows stay (as the last result does while a search is in flight);
  // before any have been drawn, the dropdown stays closed. With the flag off nothing is hidden.
  // A hung price request holds the rows for LIST_PRICE_HOLD_MS at most (fail open): then the
  // candidates still unanswered are drawn in their loading state. The deadline restarts with a
  // new result or price key.
  const [holdExpired, setHoldExpired] = useState<{
    readonly loaded: typeof loaded;
    readonly priceKey: string | null;
    readonly waiting: ReadonlySet<string>;
  } | null>(null);
  const drawnWhileWaiting =
    holdExpired !== null && holdExpired.loaded === loaded && holdExpired.priceKey === priceKey
      ? holdExpired.waiting
      : NONE_WAITING;
  // The rows this result has drawn, under this price key (AC2, as `useListedProducts`' `drawn`
  // set): a later answer never takes one away. A drawn row's Retry can come back hidden, and
  // without this the row would vanish and the walk would reach a later candidate that was never
  // priced. A new result or price key, or closing the dropdown, decides its rows afresh.
  const drawnRef = useRef<{
    readonly loaded: typeof loaded;
    readonly priceKey: string | null;
    readonly skus: Set<string>;
  }>({ loaded: null, priceKey: null, skus: new Set() });
  const drawnLatch = drawnRef.current;
  if (
    (!open && drawnLatch.skus.size > 0) ||
    drawnLatch.loaded !== loaded ||
    drawnLatch.priceKey !== priceKey
  ) {
    drawnRef.current = { loaded, priceKey, skus: new Set() };
  }
  const drawnRows = drawnRef.current.skus;
  const walked: SuggestionRows | null =
    loaded === null
      ? null
      : priceKey === null
        ? { settling: false, rows: loaded.products.slice(0, MAX_RESULTS) }
        : suggestionRows(
            loaded.products,
            (sku) => (dropdownPrices?.key === priceKey ? dropdownPrices.entries[sku] : undefined),
            (sku) => drawnRows.has(sku) || drawnWhileWaiting.has(sku),
            marginShown,
          );
  if (open && walked !== null && !walked.settling) {
    for (const row of walked.rows) drawnRows.add(row.sku);
  }
  const rowsSettling = walked?.settling === true;
  useEffect(() => {
    if (!rowsSettling || loaded === null) return;
    const timer = setTimeout(() => {
      const held = dropdownPricesRef.current?.key === priceKey ? dropdownPricesRef.current : null;
      const waiting = new Set(
        loaded.products.map((p) => p.sku).filter((sku) => held?.entries[sku] === undefined),
      );
      setHoldExpired({ loaded, priceKey, waiting });
    }, LIST_PRICE_HOLD_MS);
    return () => clearTimeout(timer);
  }, [rowsSettling, loaded, priceKey]);
  const lastRowsRef = useRef<readonly SearchResultProduct[] | null>(null);
  if (walked === null) lastRowsRef.current = null;
  else if (!walked.settling) lastRowsRef.current = walked.rows;
  const shownRows = walked !== null && !walked.settling ? walked.rows : lastRowsRef.current;

  // Hydrate availability for the on-screen suggestions (2026-07-17 demo
  // feedback), the same batch hook as the listings; no-op when unconfigured. The first candidates
  // are asked at once, beside their prices; a row hiding brings in is added once it is drawn.
  // With no active entry the prompt replaces the rows (Enabler E7-C, ADO-2274, D-1), so a result
  // still held from the last entry asks for nothing.
  useAvailability(
    catalogueEntryKey === null
      ? []
      : [
          ...new Set([
            ...(loaded?.products.slice(0, MAX_RESULTS) ?? []).map((p) => p.sku),
            ...(shownRows ?? []).map((p) => p.sku),
          ]),
        ],
  );

  // Closing the dropdown drops its prices: the reuse is not a cache (ADO-2248 DEC-3).
  useEffect(() => {
    if (open) return;
    dropdownPricesRef.current = null;
    setDropdownPrices(null);
  }, [open]);

  // The failed state's Retry (ADO-2248 PS-4): ask again for one row's SKU only. The row shows
  // loading meanwhile, then its price or the failed state again. The answer is dropped when
  // the dropdown closed or its entry or account changed in the meantime.
  const retryPrice = (sku: string): void => {
    if (entryKey === null || priceKey === null) return;
    const held = dropdownPricesRef.current;
    if (held?.key !== priceKey) return;
    const write = (entries: Readonly<Record<string, PriceEntry>>): void => {
      const next: DropdownPrices = { key: priceKey, entries };
      dropdownPricesRef.current = next;
      setDropdownPrices(next);
    };
    write({ ...held.entries, [sku]: { state: 'loading' } });
    void fetchPriceQuotes(entryKey, accountCode, [sku]).then(({ quotes, unreadSkus }) => {
      const current = dropdownPricesRef.current;
      if (current?.key !== priceKey) return;
      const entries: Record<string, PriceEntry> = { ...current.entries };
      for (const quote of quotes) entries[quote.skuCode] = priceEntryFromQuote(quote);
      for (const unread of unreadSkus) entries[unread] = { state: 'failed' };
      write(entries);
    });
  };

  // Debounced, abort-superseded suggestion fetch: the latest term always wins. With real
  // prices on, the shown rows are priced on the same signal once the search answers.
  useEffect(() => {
    if (!open || trimmed === '' || catalogueEntryKey === null) return;
    const controller = new AbortController();

    const priceRows = async (candidates: readonly SearchResultProduct[]): Promise<void> => {
      if (entryKey === null || priceKey === null) return;
      const held = (): Readonly<Record<string, PriceEntry>> =>
        dropdownPricesRef.current?.key === priceKey ? dropdownPricesRef.current.entries : {};
      /** Ask for `skus` and write their answers; false when the search was superseded. */
      const ask = async (skus: readonly string[]): Promise<boolean> => {
        const { quotes, unreadSkus } = await fetchPriceQuotes(
          entryKey,
          accountCode,
          skus,
          controller.signal,
        );
        // Superseded or closed: the answer belongs to a search nobody is looking at.
        if (controller.signal.aborted) return false;
        const entries: Record<string, PriceEntry> = { ...held() };
        for (const quote of quotes) {
          entries[quote.skuCode] = priceEntryFromQuote(quote);
        }
        for (const sku of unreadSkus) entries[sku] = { state: 'failed' };
        const next: DropdownPrices = { key: priceKey, entries };
        dropdownPricesRef.current = next;
        setDropdownPrices(next);
        return true;
      };
      // The rows it would show: reuse an answer already held (priced or unpriced); ask again
      // for a failed one.
      const firstRows = candidates
        .slice(0, MAX_RESULTS)
        .map((p) => p.sku)
        .filter((sku) => held()[sku] === undefined || held()[sku]?.state === 'failed');
      if (firstRows.length > 0 && !(await ask(firstRows))) return;
      // ADO-3948: hiding left places to fill, so ask once for every later candidate.
      if (
        !suggestionRows(candidates, (sku) => held()[sku], NONE_DRAWN, marginShownRef.current)
          .settling
      )
        return;
      const laterRows = candidates
        .slice(MAX_RESULTS)
        .map((p) => p.sku)
        .filter((sku) => held()[sku] === undefined);
      if (laterRows.length > 0) await ask(laterRows);
    };

    const timer = setTimeout(() => {
      searchSuggestions(catalogueEntryKey, trimmed, controller.signal)
        .then((result) => {
          // Story 8.2 (ADO-2011), AC6: a `notAvailable` (discontinued exact hit)
          // row has no Add anywhere, and every suggestion row offers one, so it is
          // dropped here; the full results page shows it as not available. The cap is
          // applied when the rows are drawn, after hiding (ADO-3948).
          const products = result.products.filter((p) => p.notAvailable !== true);
          setLoaded({ term: trimmed, products });
          return priceRows(products);
        })
        .catch((err: unknown) => {
          // A superseded/closed fetch aborts — that is NOT an error. (DOMException
          // extends Error; the name check covers every runtime's abort shape.)
          if (err instanceof Error && err.name === 'AbortError') return;
          setLoaded(null); // silent close — never a broken dropdown
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, trimmed, catalogueEntryKey, entryKey, accountCode, priceKey]);

  // A cleared box drops stale results, so reopening blank shows recents only.
  useEffect(() => {
    if (trimmed === '') setLoaded(null);
  }, [trimmed]);

  // Blank-query mode: the last 5 committed terms (re-read on every open/blank
  // transition so a just-committed term appears next time).
  const recents = useMemo(
    () => (open && trimmed === '' ? readRecentSearches() : []),
    [open, trimmed],
  );

  const visible = open && (trimmed === '' ? recents.length > 0 : shownRows !== null);

  // Escape (focus stays in the input — we never move it) and click-outside
  // close. "Inside" is the search anchor: the input shell + this dropdown.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Claim the Escape so a layer listening further up the bubble chain (the
      // upsell toast, on `window`) does not ALSO act on it — an Escape that
      // closes this dropdown must not close a toast the user meant to keep.
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    const onDown = (e: MouseEvent) => {
      const anchor = hostRef.current?.parentElement;
      // A modal dialog layered over the page is not "outside": a press inside it
      // answers that dialog, so it must not also close the dropdown underneath.
      if (e.target instanceof Element && e.target.closest('[aria-modal="true"]') !== null) return;
      if (anchor && !anchor.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, onClose]);

  // Enabler E7-C (ADO-2274), D-1: with no active entry a typed term asks nothing (the fetch above
  // returns), so the dropdown says why instead of staying blank. It closes as the dropdown does.
  const promptsForEntry = open && trimmed !== '' && catalogueEntryKey === null;

  return (
    <div ref={hostRef} className={styles.host}>
      {promptsForEntry && (
        <div className={styles.dropdown}>
          <NoEntryPrompt className={styles.noEntry} />
        </div>
      )}
      {visible && !promptsForEntry && (
        <div
          id={listboxId}
          role="group"
          aria-label="Search suggestions"
          className={styles.dropdown}
        >
          {trimmed === '' ? (
            <div className={styles.recentsSection}>
              <div className={styles.sectionLabel}>Recent searches</div>
              {recents.map((term) => (
                <button
                  key={term}
                  type="button"
                  className={styles.recentRow}
                  onClick={() => onCommit(term)}
                >
                  <Icon name="clockCounterClockwise" size={18} />
                  <span className={styles.recentTerm}>{term}</span>
                </button>
              ))}
            </div>
          ) : shownRows !== null && shownRows.length === 0 ? (
            <div className={styles.empty}>No products found. Try a different search.</div>
          ) : (
            <>
              {shownRows?.map((p) => (
                <TypeaheadResultRow
                  key={p.sku}
                  product={p}
                  price={
                    dropdownPrices?.key === priceKey ? dropdownPrices.entries[p.sku] : undefined
                  }
                  priceAsked={priceKey !== null}
                  onRetryPrice={retryPrice}
                  onCommit={onCommit}
                />
              ))}
              {/* Commits the current query — same as pressing Enter. */}
              <button type="button" className={styles.viewAll} onClick={() => onCommit(query)}>
                View all
                <Icon name="arrowRight" size={16} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchTypeahead;
